import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, api, readTableFile } from '../api/client.js'
import { useSession } from '../api/session.js'
import type { ObjectNode } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { TABLE_ACCEPT } from '../components/ListImport.js'
import { PersonPicker } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { Field, Modal, Notice, Tile } from '../components/ui.js'
import { formatDate, formatDateTime, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { CallbackForm, TaskModal } from './TaskDialogs.js'
import {
  ColumnPicker,
  GROUP_LABEL,
  LEGIONELLA_COLUMNS,
  TaskTable,
  exportUrl,
  useColumns,
  type Dir,
  type GroupBy,
  type SortKey,
} from './TaskTable.js'

interface MonthResponse {
  month: string
  summary: {
    total: number
    done: number
    open: number
    missed: number
    callback: number
    callbackResolved: number
    defects: number
    byOption: { option: string; count: number }[]
  }
  toResolve: {
    id: string
    street: string | null
    zip: string | null
    city: string | null
    unitLabel: string | null
    textOption: string | null
    note: string | null
    defectNote: string | null
    completedAtClient: string | null
    assigneeId: string | null
    assigneeName: string | null
    assigneePhone: string | null
    listMonth: string | null
  }[]
}

interface LegionellaTemplate {
  id: string
  name: string
  textOptions: string[]
  callbackOptions: string[]
}

interface ImportPreview {
  rows: number
  counts: { ok: number; duplicate: number; bad_date: number }
  preview: {
    line: number
    state: 'ok' | 'duplicate' | 'bad_date'
    message: string
    address: string
    unitLabel: string | null
    objectLabel: string | null
    assigneeName: string | null
    dueDate: string | null
  }[]
  created?: number
}

type Show = '' | 'offen' | 'ueberfaellig' | 'erledigt' | 'rueckruf'
const SHOW_FILTER: Record<Exclude<Show, ''>, [string, string]> = {
  offen: ['statuses', 'open'],
  ueberfaellig: ['statuses', 'missed'],
  erledigt: ['statuses', 'done'],
  rueckruf: ['hint', 'rueckruf'],
}

const monthLabel = (month: string) => {
  const [y, m] = month.split('-')
  return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(new Date(Number(y), Number(m) - 1, 15))
}
const shiftMonth = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(y, m - 1 + by, 1))
  return d.toISOString().slice(0, 7)
}

/**
 * Legionellenprophylaxe als eigener Bereich (Workshop 23.10.). Organisiert wird
 * nach Mitarbeitern, nicht nach Gebäuden: die Monatsliste der Leerwohnungen
 * einlesen, sehen, wie weit jeder ist, die Rückrufe klären, und am Monatsende
 * der Report je Kunde für die Abrechnung. Die Liste selbst ist die
 * Aufgabenliste, auf den Monat gefiltert, mit Sortierung, Spalten und Export.
 */
export function LegionellaPage() {
  const [params, setParams] = useSearchParams()
  const session = useSession()
  const month = params.get('monat') ?? todayIso().slice(0, 7)
  const customerId = params.get('kunde') ?? ''
  const show = (params.get('zeige') ?? '') as Show
  const q = params.get('suche') ?? ''
  const grouping = params.get('gruppieren') ?? 'assignee'
  const groupBy = (grouping === 'assignee' || grouping === 'city' ? grouping : null) as GroupBy | null
  const openGroups = (params.get('auf') ?? '').split(',').filter(Boolean)
  const sortKey = (params.get('sort') as SortKey | null) ?? 'address'
  const dir = (params.get('richtung') as Dir | null) ?? 'asc'
  const page = Math.max(1, Number(params.get('seite') ?? '1') || 1)
  const [importing, setImporting] = useState(false)
  const [resolving, setResolving] = useState<string | null>(null)
  const [openTask, setOpenTask] = useState<string | null>(null)
  const [text, setText] = useState(q)
  const queryClient = useQueryClient()
  const toast = useToast()
  const columns = useColumns(`hauswerk.legionellaColumns.${session.data?.user.id ?? 'anon'}`, LEGIONELLA_COLUMNS)

  function update(changes: Record<string, string | null | undefined>, options: { keepPage?: boolean } = {}) {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    if (!options.keepPage) next.delete('seite')
    setParams(next, { replace: true })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (text.trim() !== q) update({ suche: text.trim() })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])
  useEffect(() => {
    if (!q) setText('')
  }, [q])

  const query = new URLSearchParams({ month })
  if (customerId) query.set('customerId', customerId)
  const data = useQuery({
    queryKey: ['legionella', query.toString()],
    queryFn: () => api<MonthResponse>(`/api/legionella?${query}`),
    placeholderData: (previous) => previous,
  })
  const customers = useQuery({ queryKey: ['objects', 'root', 'aktiv'], queryFn: () => api<ObjectNode[]>('/api/objects?root=true') })

  const filter = new URLSearchParams({ listMonth: month, legionella: '1' })
  if (customerId) filter.set('objectId', customerId)
  if (show) filter.set(...SHOW_FILTER[show])
  if (q) filter.set('q', q)

  const summary = data.data?.summary
  const toResolve = data.data?.toResolve ?? []
  const filtered = Boolean(customerId || show || q)
  const billed = summary?.byOption.reduce((sum, o) => sum + o.count, 0) ?? 0

  function resetFilters() {
    const next = new URLSearchParams()
    for (const key of ['monat', 'gruppieren', 'sort', 'richtung']) {
      const value = params.get(key)
      if (value) next.set(key, value)
    }
    setText('')
    setParams(next, { replace: true })
  }

  const tile = (key: Show, label: string, value: number | undefined, tone?: 'good' | 'alert', sub?: string) => (
    <button
      type="button"
      className={`tile-button ${show === key ? 'active' : ''}`}
      aria-pressed={show === key}
      onClick={() => update({ zeige: show === key ? '' : key })}
    >
      <Tile label={label} value={value ?? 0} tone={tone} sub={sub} />
    </button>
  )

  return (
    <>
      <PageHead
        title="Legionellen"
        subtitle="Monatsliste der Leerwohnungen, nach Mitarbeiter. Rückrufe klären, am Monatsende der Report für die Abrechnung."
        action={
          <div className="actions">
            <a className="btn secondary" href={`/api/legionella/report.xlsx?${query}`}>
              <Icon name="download" /> Monatsreport (Excel)
            </a>
            <button type="button" className="btn" onClick={() => setImporting(true)}>
              <Icon name="upload" /> Monatsliste einlesen
            </button>
          </div>
        }
      />

      <div className="toolbar">
        <div className="toolbar-row">
          <div className="month-step" role="group" aria-label="Monat">
            <button type="button" className="btn ghost small" aria-label="Monat zurück" onClick={() => update({ monat: shiftMonth(month, -1), auf: null })}>
              <Icon name="chevronLeft" size={16} />
            </button>
            <strong>{monthLabel(month)}</strong>
            <button type="button" className="btn ghost small" aria-label="Monat vor" onClick={() => update({ monat: shiftMonth(month, 1), auf: null })}>
              <Icon name="chevronRight" size={16} />
            </button>
            {month !== todayIso().slice(0, 7) && (
              <button type="button" className="btn ghost small" onClick={() => update({ monat: null, auf: null })}>
                aktueller Monat
              </button>
            )}
          </div>
          <label className="inline-field">
            <span className="muted small">Kunde</span>
            <select value={customerId} onChange={(e) => update({ kunde: e.target.value, auf: null })}>
              <option value="">Alle Kunden</option>
              {customers.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {summary && (
        <div className="tiles" style={{ marginBottom: 18 }}>
          {tile('', 'Wohnungen', summary.total)}
          {tile('erledigt', 'Erledigt', summary.done, summary.total > 0 && summary.done === summary.total ? 'good' : undefined)}
          {tile('offen', 'Offen', summary.open)}
          {tile('ueberfaellig', 'Überfällig', summary.missed, summary.missed > 0 ? 'alert' : undefined)}
          {tile(
            'rueckruf',
            'Rückruf offen',
            summary.callback,
            summary.callback > 0 ? 'alert' : undefined,
            summary.callbackResolved > 0 ? `${summary.callbackResolved} geklärt` : undefined,
          )}
        </div>
      )}

      <div className="legionella-top">
        {toResolve.length > 0 && (
          <section className="card">
            <div className="card-head">
              <h2>Zu klären ({toResolve.length})</h2>
              <span className="muted small">alle Monate, Mitarbeiter anrufen, dann „Geklärt“</span>
            </div>
            <ul className="callback-list">
              {toResolve.map((t) => (
                <li key={t.id}>
                  <button type="button" className="linklike" style={{ flex: '1 1 220px', textAlign: 'left' }} onClick={() => setOpenTask(t.id)}>
                    <span className="strong">
                      {t.street}
                      {t.unitLabel ? `, ${t.unitLabel}` : ''}
                    </span>
                    <div className="small">
                      {t.textOption}
                      {t.listMonth && t.listMonth !== month && <span className="badge" style={{ marginLeft: 6 }}>Liste {monthLabel(t.listMonth)}</span>}
                    </div>
                    {t.defectNote && <div className="small text-bad">Mangel: {t.defectNote}</div>}
                    {t.note && <div className="small muted">{t.note}</div>}
                  </button>
                  <div className="small" style={{ flex: '0 1 180px' }}>
                    {t.assigneeId ? <Link to={`/mitarbeiter/${t.assigneeId}`}>{t.assigneeName}</Link> : (t.assigneeName ?? 'ohne Zuordnung')}
                    {t.assigneePhone && (
                      <div>
                        <a href={`tel:${t.assigneePhone.replace(/\s+/g, '')}`}>{t.assigneePhone}</a>
                      </div>
                    )}
                    {t.completedAtClient && <div className="muted">{formatDateTime(t.completedAtClient)}</div>}
                  </div>
                  <button type="button" className="btn small" onClick={() => setResolving(t.id)}>
                    Geklärt
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {summary && summary.byOption.length > 0 && (
          <section className="card">
            <div className="card-head">
              <h2>Ergebnisse</h2>
              <span className="muted small">Grundlage der Abrechnung</span>
            </div>
            <div className="card-body result-bars">
              {summary.byOption.map((o) => (
                <div key={o.option} className="row-bar small">
                  <span>{o.option}</span>
                  <span className="track" aria-hidden="true">
                    <span style={{ width: `${billed ? Math.round((o.count / billed) * 100) : 0}%` }} />
                  </span>
                  <strong>{o.count}</strong>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <section className="card task-filters">
        <div className="task-filter-row">
          <input
            type="search"
            className="task-search"
            placeholder="Suchen: Adresse, Wohnung, Mitarbeiter, Ergebnis"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Wohnungen durchsuchen"
          />
          <label className="inline-field">
            <span className="muted small">Gruppieren</span>
            <select value={groupBy ?? 'ohne'} onChange={(e) => update({ gruppieren: e.target.value === 'assignee' ? null : e.target.value, auf: null })} aria-label="Gruppieren">
              <option value="assignee">nach {GROUP_LABEL.assignee}</option>
              <option value="city">nach {GROUP_LABEL.city}</option>
              <option value="ohne">ohne</option>
            </select>
          </label>
          <ColumnPicker columns={columns} />
          <a className="btn secondary small" href={exportUrl(filter, columns.shown, 'xlsx')} title="Genau diese Liste mit den sichtbaren Spalten">
            <Icon name="download" size={14} /> Liste als Excel
          </a>
          {filtered && (
            <button type="button" className="btn ghost small reset-filters" onClick={resetFilters}>
              <Icon name="close" size={14} /> Alle Filter zurücksetzen
            </button>
          )}
        </div>
      </section>

      <section className="card">
        <TaskTable
          filter={filter}
          columns={columns.shown}
          sortKey={sortKey}
          dir={dir}
          onSort={(key, d) => update({ sort: key, richtung: d })}
          page={page}
          onPage={(p) => update({ seite: String(p) }, { keepPage: true })}
          groupBy={groupBy}
          openGroups={openGroups}
          onOpenGroups={(keys) => update({ auf: keys.join(',') }, { keepPage: true })}
          emptyTitle={summary?.total ? 'Nichts in dieser Auswahl' : `Keine Monatsliste für ${monthLabel(month)}`}
          emptyHint={summary?.total ? 'Filter oben ändern oder zurücksetzen.' : 'Die Liste der Leerwohnungen vom Kunden als Excel einlesen.'}
          scrollKey={`hauswerk.legionellaScroll.${params.toString()}`}
        />
      </section>

      <LegionellaImportModal
        open={importing}
        month={month}
        customerId={customerId}
        customers={customers.data ?? []}
        onClose={() => setImporting(false)}
        onDone={() => {
          setImporting(false)
          void queryClient.invalidateQueries({ queryKey: ['legionella'] })
          void queryClient.invalidateQueries({ queryKey: ['occurrences'] })
        }}
      />
      <Modal open={resolving !== null} title="Rückruf geklärt" onClose={() => setResolving(null)}>
        {resolving && (
          <CallbackForm
            taskId={resolving}
            onCancel={() => setResolving(null)}
            onDone={(message) => {
              setResolving(null)
              toast(message)
              void queryClient.invalidateQueries({ queryKey: ['legionella'] })
              void queryClient.invalidateQueries({ queryKey: ['occurrences'] })
            }}
          />
        )}
      </Modal>
      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={(message) => {
          if (message) toast(message)
          void queryClient.invalidateQueries({ queryKey: ['legionella'] })
          void queryClient.invalidateQueries({ queryKey: ['occurrences'] })
        }}
      />
    </>
  )
}

const STATE_LABEL: Record<ImportPreview['preview'][number]['state'], { label: string; badge: string }> = {
  ok: { label: 'wird angelegt', badge: 'done' },
  duplicate: { label: 'schon da', badge: '' },
  bad_date: { label: 'Datum', badge: 'warn' },
}

/** The customer's list as it comes: Straße, Hausnummer, PLZ, Lage, Wohnung, Mitarbeiter, Fällig. */
function LegionellaImportModal({
  open,
  month,
  customerId: pageCustomer,
  customers,
  onClose,
  onDone,
}: {
  open: boolean
  month: string
  customerId: string
  customers: ObjectNode[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const templates = useQuery({ queryKey: ['legionella-templates'], queryFn: () => api<LegionellaTemplate[]>('/api/legionella/templates'), enabled: open })
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [listMonth, setListMonth] = useState(month)
  const [assigneeId, setAssigneeId] = useState('')
  const [customerId, setCustomerId] = useState('')
  const chosenCustomer = customerId || pageCustomer || customers[0]?.id || ''
  const [result, setResult] = useState<ImportPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const chosenTemplate = templateId || templates.data?.[0]?.id || ''

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<ImportPreview>('/api/legionella/import', {
        method: 'POST',
        body: { csv, templateId: chosenTemplate, month: listMonth, customerId: chosenCustomer, assigneeId: assigneeId || undefined, dryRun },
      }),
    onSuccess: (data, dryRun) => {
      setError(null)
      if (dryRun) setResult(data)
      else {
        toast(`${data.created ?? 0} Wohnungen für ${monthLabel(listMonth)} angelegt.`)
        reset()
        onDone()
      }
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Einlesen fehlgeschlagen.'),
  })

  function reset() {
    setCsv('')
    setFileName('')
    setResult(null)
    setError(null)
  }

  async function pick(file: File | undefined) {
    if (!file) return
    try {
      setCsv(await readTableFile(file))
      setFileName(file.name)
      setResult(null)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Die Datei konnte nicht gelesen werden.')
    }
  }

  return (
    <Modal
      open={open}
      title="Monatsliste einlesen"
      onClose={() => {
        reset()
        onClose()
      }}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={() => { reset(); onClose() }}>
            Abbrechen
          </button>
          {result ? (
            <button type="button" className="btn" disabled={run.isPending || result.counts.ok === 0} onClick={() => run.mutate(false)}>
              {result.counts.ok} Wohnungen anlegen
            </button>
          ) : (
            <button type="button" className="btn" disabled={!csv || !chosenTemplate || !chosenCustomer || run.isPending} onClick={() => run.mutate(true)}>
              Prüfen
            </button>
          )}
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {templates.data && templates.data.length === 0 && (
        <Notice kind="warn">Noch keine Leistung als Legionellen-Leistung markiert. Unter Leistungen bei der Prüfung das Häkchen setzen.</Notice>
      )}
      <p className="small muted" style={{ marginTop: 0 }}>
        Excel oder CSV mit den Spalten Straße, Hausnummer, PLZ, Ort, Lage, Wohnung, Mitarbeiter und Fällig. Es wird nichts im
        Objektbaum angelegt: jede Zeile wird eine Aufgabe mit Adresse und Wohnung. Gibt es das Haus beim Kunden schon als Gebäude,
        hängt die Aufgabe dort und steht in dessen Verlauf. Ohne Fälligkeit gilt der Monatsletzte.
      </p>
      <div className="row">
        <Field label="Kunde">
          <select value={chosenCustomer} onChange={(e) => { setCustomerId(e.target.value); setResult(null) }}>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Datei">
          <input type="file" accept={TABLE_ACCEPT} onChange={(e) => void pick(e.target.files?.[0])} />
        </Field>
        <Field label="Monat">
          <input type="month" value={listMonth} onChange={(e) => { if (e.target.value) { setListMonth(e.target.value); setResult(null) } }} />
        </Field>
      </div>
      <div className="row">
        <Field label="Leistung">
          <select value={chosenTemplate} onChange={(e) => { setTemplateId(e.target.value); setResult(null) }}>
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Mitarbeiter, wenn die Liste keinen nennt">
          <PersonPicker value={assigneeId} onChange={(id) => { setAssigneeId(id); setResult(null) }} emptyLabel="Ohne Zuordnung" dense />
        </Field>
      </div>
      {fileName && !result && <p className="small">Datei: {fileName}</p>}
      {result && (
        <>
          <p className="small">
            <strong>{result.counts.ok}</strong> werden angelegt, {result.counts.duplicate} schon in der Liste
            {result.counts.bad_date ? <span className="text-bad">, {result.counts.bad_date} mit unlesbarem Datum</span> : ''}.
          </p>
          <div className="table-scroll" style={{ maxHeight: 320 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Adresse</th>
                  <th>Wohnung</th>
                  <th>Gebäude</th>
                  <th>Fällig</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {result.preview.map((p) => (
                  <tr key={p.line}>
                    <td className="small muted">{p.line}</td>
                    <td className="small">{p.address}</td>
                    <td className="small">{p.unitLabel ?? '–'}</td>
                    <td className="small">{p.objectLabel ?? <span className="muted">ohne Gebäude</span>}</td>
                    <td className="small nowrap">{p.dueDate ? formatDate(p.dueDate) : '–'}</td>
                    <td className="nowrap">
                      <span className={`badge ${STATE_LABEL[p.state].badge}`} title={p.message}>
                        {STATE_LABEL[p.state].label}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}
